// A person's remembered choices, kept on the server so they follow the
// person across browsers and devices (owner, 2026-09-18: "the remember per
// project / last project selected on all tabs should be by user").
//
// The server (GET/PUT /user-preferences, models/userPreference) is the
// truth; this browser keeps a copy per environment and per user only so a
// page can open on the remembered value before the request returns.
//
//   const [zoom, setZoom] = usePreference("gantt.zoom", 3);
//   const [lanes, setLanes] = usePreference("gantt.collapsed", [], { projectId });
//   const [tab, setTab] = usePreference("reviewCenter.tab", "proposals", { legacyKey: "old_local_key" });
//
// `legacyKey`: a browser key the page used before. When the server has
// nothing yet, that value (this person's own, on this device) seeds the
// server once, so nobody loses a setting in the move.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { url, tokenRef } from "api";

const ENV = String(tokenRef).replace(/_token$/, "");
const cacheKey = (userId) => `${ENV}_prefs:${userId || "anon"}`;
const EVENT = "bl:prefs";

function readCache(userId) {
  try { return JSON.parse(localStorage.getItem(cacheKey(userId)) || "null") || { g: {}, p: {} }; } catch (e) { return { g: {}, p: {} }; }
}
function writeCache(userId, cache) {
  try { localStorage.setItem(cacheKey(userId), JSON.stringify(cache)); } catch (e) { /* storage full or off */ }
}
const scopeOf = (cache, projectId) => (projectId ? (cache.p[projectId] = cache.p[projectId] || {}) : cache.g);

export function getPref(userId, key, projectId = "") {
  const scope = readCache(userId)[projectId ? "p" : "g"];
  const bag = projectId ? (scope || {})[projectId] || {} : scope || {};
  return Object.prototype.hasOwnProperty.call(bag, key) ? bag[key] : undefined;
}

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}`, "Content-Type": "application/json" });

// One request per (user, scope) at a time; later callers share it.
const inflight = new Map();
const timers = new Map(); // pending writes: `${userId}|${projectId}|${key}`
export function loadPrefs(userId, projectId = "") {
  if (!userId || !localStorage.getItem(tokenRef)) return Promise.resolve(null);
  const k = `${userId}|${projectId}`;
  if (inflight.has(k)) return inflight.get(k);
  const p = fetch(`${url}/user-preferences${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`, { headers: auth() })
    .then((r) => (r.ok ? r.json() : null))
    .then((body) => {
      if (!body) return null;
      const cache = readCache(userId);
      // A change still waiting to be sent is newer than what the server said.
      const keep = (scopeId, bag, fresh) => {
        const out = { ...(fresh || {}) };
        for (const t of timers.keys()) {
          const [u, p, k] = t.split("|");
          if (u === String(userId) && p === scopeId && Object.prototype.hasOwnProperty.call(bag || {}, k)) out[k] = bag[k];
        }
        return out;
      };
      cache.g = keep("", cache.g, body.values);
      if (projectId) cache.p[projectId] = keep(projectId, cache.p[projectId], body.project);
      writeCache(userId, cache);
      try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { userId, projectId } })); } catch (e) { /* ignore */ }
      return body;
    })
    .catch(() => null)
    .finally(() => { setTimeout(() => inflight.delete(k), 1500); });
  inflight.set(k, p);
  return p;
}

// Writes are coalesced per key so dragging a column does not send a
// request per pixel (timers: declared above).
export function setPref(userId, key, value, projectId = "") {
  const cache = readCache(userId);
  const bag = scopeOf(cache, projectId);
  if (value === null || value === undefined) delete bag[key]; else bag[key] = value;
  writeCache(userId, cache);
  if (!userId || !localStorage.getItem(tokenRef)) return;
  const t = `${userId}|${projectId}|${key}`;
  clearTimeout(timers.get(t));
  timers.set(t, setTimeout(() => {
    timers.delete(t);
    fetch(`${url}/user-preferences`, { method: "PUT", headers: auth(), body: JSON.stringify({ key, value: value === undefined ? null : value, projectId: projectId || "" }) })
      .catch(() => { /* the cache still has it; the next change retries */ });
  }, 400));
}

function readLegacy(legacyKey) {
  if (!legacyKey) return undefined;
  try {
    const raw = localStorage.getItem(legacyKey);
    if (raw === null) return undefined;
    try { return JSON.parse(raw); } catch (e) { return raw; }
  } catch (e) { return undefined; }
}

// The signed-in user's id from the stored token, for helpers that are not
// React components (the token's payload carries `id`).
export function currentUserId() {
  try {
    const t = localStorage.getItem(tokenRef);
    if (!t) return "";
    const b64 = t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const p = JSON.parse(decodeURIComponent(escape(atob(b64))));
    return String(p.id || p._id || "");
  } catch (e) { return ""; }
}

// The signed-in user from the store; outside a <Provider> (a component
// rendered on its own, a test) the token's id, rather than a crash.
const idIn = (store) => {
  const info = store && store.getState().user && store.getState().user.userInfo;
  return info ? String(info._id || info.id || "") : "";
};
// A page rendered without react-redux's context (a test that mocks the
// module, a page mounted outside the Provider) reads the empty one instead of
// throwing; the user id then comes from currentUserId() as below.
const NO_STORE = createContext(null);
export function useUserId() {
  const store = (useContext(ReactReduxContext || NO_STORE) || {}).store || null;
  const [id, setId] = useState(() => (store ? idIn(store) : currentUserId()));
  useEffect(() => {
    if (!store) return undefined;
    setId(idIn(store));
    return store.subscribe(() => setId(idIn(store)));
  }, [store]);
  return id;
}

export function usePreference(key, defaultValue, { projectId = "", legacyKey = "" } = {}) {
  const userId = useUserId();
  const pid = projectId ? String(projectId) : "";
  const initial = () => {
    const v = getPref(userId, key, pid);
    if (v !== undefined) return v;
    const legacy = readLegacy(legacyKey);
    return legacy !== undefined ? legacy : defaultValue;
  };
  const [value, setValue] = useState(initial);
  const valueRef = useRef(value);
  valueRef.current = value;

  // A new user or project: start from what is known for it.
  useEffect(() => { setValue(initial()); }, [userId, pid, key]); // eslint-disable-line

  // The server is the truth: take its value; seed it from the legacy key
  // once when it has none.
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    loadPrefs(userId, pid).then((body) => {
      if (!alive || !body) return;
      const bag = pid ? body.project || {} : body.values || {};
      if (Object.prototype.hasOwnProperty.call(bag, key)) {
        const v = getPref(userId, key, pid); // the cache keeps a change still being sent
        if (v !== undefined) setValue(v);
        return;
      }
      const legacy = readLegacy(legacyKey);
      if (legacy !== undefined) setPref(userId, key, legacy, pid);
    });
    const onPrefs = (e) => {
      if (!alive || !e.detail || e.detail.userId !== userId) return;
      const v = getPref(userId, key, pid);
      if (v !== undefined && JSON.stringify(v) !== JSON.stringify(valueRef.current)) setValue(v);
    };
    window.addEventListener(EVENT, onPrefs);
    return () => { alive = false; window.removeEventListener(EVENT, onPrefs); };
  }, [userId, pid, key]); // eslint-disable-line

  const set = useCallback((next) => {
    const v = typeof next === "function" ? next(valueRef.current) : next;
    valueRef.current = v; // back-to-back updates in one handler build on each other
    setValue(v);
    setPref(userId, key, v, pid);
  }, [userId, key, pid]);
  return [value, set];
}
